import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import { PageContainer } from './pageContainer';
import { Icon, Tabs } from '@shared/widgets';
import LockIcon from '@assets/icons/lock.svg';

const Tab = {
  NewRequest: 'New Request',
  MyRequests: 'My Requests',
} as const;
type Tab = (typeof Tab)[keyof typeof Tab];

export const TravelLayout = () => {
  const { navigate } = useRouteHook();
  const { pathname } = useLocation();

  const profileStore = useUnit($profileStore.store);

  const { t } = useTranslation('travel');

  useEffect(() => {
    if (profileStore.data?.enrolledInCorporateTravelServices) {
      if (pathname.includes(RoutePaths.travelNewRequest.path) || pathname.includes(RoutePaths.travelMyRequests.path)) return;
      navigate(RoutePaths.travelNewRequest.onNavigate(), { replace: true });
      return;
    }

    if (pathname === RoutePaths.travel.onNavigate() || pathname.includes(RoutePaths.travelEnrol.path)) return;
    navigate(RoutePaths.travel.onNavigate(), { replace: true });
  }, [pathname, navigate]);

  const tab = useMemo(() => {
    if (pathname.includes(RoutePaths.travelNewRequest.path)) return Tab.NewRequest;
    if (pathname.includes(RoutePaths.travelMyRequests.path)) return Tab.MyRequests;
    return undefined;
  }, [pathname]);

  const changeTab = (nextTab: Tab) => {
    switch (nextTab) {
      case Tab.NewRequest:
        navigate(RoutePaths.travelNewRequest.onNavigate());
        break;
      case Tab.MyRequests:
        navigate(RoutePaths.travelMyRequests.onNavigate());
        break;
      default: {
        const _: never = nextTab;
        return _;
      }
    }
  };

  const tabs = [
    {
      label: !tab ? (
        <div className='flex items-center gap-1'>
          {t('tabs.newRequest')}
          <Icon icon={<LockIcon className='size-4' />} />
        </div>
      ) : (
        t('tabs.newRequest')
      ),
      value: Tab.NewRequest,
      disabled: !tab,
    },
    {
      label: !tab ? (
        <div className='flex items-center gap-1'>
          {t('tabs.myRequests')}
          <Icon icon={<LockIcon className='size-4' />} />
        </div>
      ) : (
        t('tabs.myRequests')
      ),
      value: Tab.MyRequests,
      disabled: !tab,
    },
  ];
  return (
    <PageContainer className='flex flex-col gap-6' title={t('title')} header={t('header')}>
      <Tabs options={tabs} value={tab} onChange={changeTab} />
      <Outlet />
    </PageContainer>
  );
};
